/**
 * V4Toolbar — Phase 7c-2.
 *
 * Replaces the in-page header block in CanvasPageV4. The previous
 * toolbar had grown to 7 buttons + 3 status indicators + 1 dropdown +
 * 1 link in a single flat row — operators got lost. This component
 * groups them into three logical zones with subtle dividers:
 *
 *   ┌─────────────────────┬───────────────────┬─────────────────────┐
 *   │ IDENTITY            │ STATUS            │ ACTIONS             │
 *   │ Phase badge ·       │ Saved 12s ago  ·  │ Run · Runs · Save · │
 *   │ Workflow selector   │ Validation chip   │ ⋯ overflow          │
 *   └─────────────────────┴───────────────────┴─────────────────────┘
 *
 * Design choices:
 *   • All colors from v4Tokens (BRAND teal, ACCENT semantic, S stone)
 *     — no more ad-hoc purple/green/coral hex codes.
 *   • Phase badge uses teal (BRAND.light + BRAND.dark) — was violet.
 *   • Run uses ACCENT.emerald (#10b981) — was lime green #15803d.
 *   • Save dirty uses ACCENT.amber — was coral #E96B3F.
 *   • Open in V3 demoted to overflow menu — low frequency, was crowding.
 *   • Re-layout demoted to overflow — also low frequency.
 *   • Save-as / New workflow demoted to overflow + workflow switcher.
 *   • Validation count chip lives inline (between status zone and
 *     actions) so the operator sees "Workflow has 2 errors" at a
 *     glance without scrolling to find a banner.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Play, History, Save, ExternalLink, MoreHorizontal, Loader2,
  AlertTriangle, AlertOctagon, RotateCcw, FileJson,
} from 'lucide-react';
import V4WorkflowSwitcher from './V4WorkflowSwitcher';
import V4RunsDropdown from './V4RunsDropdown';
import {
  BRAND, S, ACCENT, SPACING, RADIUS, FONT, WEIGHT, SHADOW, Z,
} from './v4Tokens';

export default function V4Toolbar({
  // Workflow context
  workflowId, workflowMeta, orgId, tenantId,
  // State
  loading, saving, dirty, executing,
  autoSaveStatus, validation,
  lastRunId,
  // Action handlers
  onRun, onSwitchWorkflow, onNew, onSaveAs, onSave, onRelayout, onOpenInV3,
  onSelectRun, onOpenValidation,
  // Hooks for hotkey/help
  v3Href,
}) {
  const errCount = validation?.stats?.error || 0;
  const warnCount = validation?.stats?.warning || 0;
  const hasIssues = errCount > 0 || warnCount > 0;

  return (
    <header style={{
      display: 'flex', alignItems: 'center',
      gap: SPACING.l,
      padding: `${SPACING.s}px ${SPACING.l}px`,
      height: 48,
      background: '#ffffff',
      borderBottom: `1px solid ${S[200]}`,
      flexShrink: 0,
    }}>

      {/* ─── IDENTITY zone ────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.m, minWidth: 0 }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center',
          fontSize: FONT.xs, fontWeight: WEIGHT.bold,
          color: BRAND.dark, background: BRAND.light,
          padding: `2px ${SPACING.s}px`,
          borderRadius: RADIUS.pill,
          letterSpacing: 0.5, textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}>
          V4 · Beta
        </span>
        <V4WorkflowSwitcher
          currentWorkflowId={workflowId}
          currentWorkflowName={workflowMeta?.name}
          onSelect={onSwitchWorkflow}
          onNew={onNew}
          onSaveAs={onSaveAs}
          canSaveAs={!!workflowId}
          orgId={orgId}
          tenantId={tenantId}
        />
      </div>

      {/* ─── STATUS zone (center, takes remaining width) ────── */}
      <div style={{
        flex: 1, minWidth: 0,
        display: 'flex', alignItems: 'center', gap: SPACING.m,
      }}>
        {loading && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: SPACING.xs,
            fontSize: FONT.sm, color: S[500],
          }}>
            <Loader2 size={11} className="v4-spin" aria-hidden="true" />
            Loading workflow…
          </span>
        )}
        {!loading && workflowId && hasIssues && (
          <ValidationChip
            errCount={errCount}
            warnCount={warnCount}
            onClick={onOpenValidation}
          />
        )}
      </div>

      {/* ─── ACTIONS zone (right) ────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.s }}>
        {/* Saved status — text-only, no button. Sits before actions
            so the operator sees state then acts. */}
        {workflowId && autoSaveStatus && (
          <span
            role="status"
            aria-live="polite"
            style={{
              fontSize: FONT.sm,
              color: dirty ? ACCENT.amber.text : S[500],
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
            }}
          >
            {autoSaveStatus}
          </span>
        )}

        {/* RUN — primary action, brand emerald */}
        {workflowId && (
          <button
            type="button"
            onClick={onRun}
            disabled={executing || loading || saving}
            title={errCount > 0 ? 'Fix validation errors before running' : 'Run workflow (⌘R)'}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: SPACING.xs,
              padding: `5px ${SPACING.m}px`,
              fontSize: FONT.sm, fontWeight: WEIGHT.semibold,
              color: '#ffffff',
              background: executing ? S[400] : ACCENT.emerald.icon,
              border: 'none', borderRadius: RADIUS.md,
              cursor: (executing || loading || saving) ? 'not-allowed' : 'pointer',
              opacity: (executing || loading || saving) ? 0.7 : 1,
              fontFamily: 'inherit',
              transition: 'all 160ms ease',
            }}
          >
            {executing
              ? <Loader2 size={11} className="v4-spin" aria-hidden="true" />
              : <Play size={11} aria-hidden="true" />
            }
            {executing ? 'Running…' : 'Run'}
          </button>
        )}

        {/* RUNS dropdown — secondary, outline */}
        {workflowId && (
          <V4RunsDropdown
            workflowId={workflowId}
            freshRunId={lastRunId}
            onSelectRun={onSelectRun}
          />
        )}

        {/* SAVE — secondary; dirty state uses amber tint */}
        {workflowId && (
          <button
            type="button"
            onClick={() => onSave && onSave()}
            disabled={saving || loading}
            title={dirty ? 'Save unsaved changes (⌘S)' : 'No unsaved changes'}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: SPACING.xs,
              padding: `5px ${SPACING.m}px`,
              fontSize: FONT.sm, fontWeight: WEIGHT.medium,
              color: dirty ? ACCENT.amber.text : S[700],
              background: dirty ? ACCENT.amber.bg : '#ffffff',
              border: `1px solid ${dirty ? ACCENT.amber.border : S[200]}`,
              borderRadius: RADIUS.md,
              cursor: (saving || loading) ? 'not-allowed' : 'pointer',
              opacity: (saving || loading) ? 0.6 : 1,
              transition: 'all 160ms ease',
              fontFamily: 'inherit',
            }}
          >
            <Save size={11} aria-hidden="true" />
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}

        {/* OVERFLOW menu — Re-layout, Save As, New, Open in V3 */}
        {workflowId && (
          <OverflowMenu
            onRelayout={onRelayout}
            onSaveAs={onSaveAs}
            onNew={onNew}
            onOpenInV3={onOpenInV3}
            v3Href={v3Href}
          />
        )}
      </div>
    </header>
  );
}

/* ───── Validation chip — clickable, opens the validation panel ──── */
function ValidationChip({ errCount, warnCount, onClick }) {
  const isError = errCount > 0;
  const tone = isError ? ACCENT.rose : ACCENT.amber;
  const Icon = isError ? AlertOctagon : AlertTriangle;
  const total = errCount + warnCount;
  const label = errCount > 0
    ? `${errCount} error${errCount === 1 ? '' : 's'}${warnCount > 0 ? ` · ${warnCount} warning${warnCount === 1 ? '' : 's'}` : ''}`
    : `${warnCount} warning${warnCount === 1 ? '' : 's'}`;
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Workflow has ${total} issue${total === 1 ? '' : 's'} — click to view`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: SPACING.xs,
        padding: `3px ${SPACING.s + 2}px`,
        fontSize: FONT.sm, fontWeight: WEIGHT.medium,
        color: tone.text, background: tone.bg,
        border: `1px solid ${tone.border}`,
        borderRadius: RADIUS.pill,
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'filter 160ms ease',
      }}
    >
      <Icon size={11} aria-hidden="true" style={{ color: tone.icon }} />
      {label}
    </button>
  );
}

/* ───── Overflow menu — low-frequency actions hidden behind ··· ──── */
function OverflowMenu({ onRelayout, onSaveAs, onNew, onOpenInV3, v3Href }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (!popoverRef.current) return;
      if (popoverRef.current.contains(e.target)) return;
      if (triggerRef.current && triggerRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const item = (label, Icon, onSelect, opts = {}) => (
    <button
      key={label}
      type="button"
      onClick={() => { setOpen(false); onSelect && onSelect(); }}
      style={{
        display: 'flex', alignItems: 'center', gap: SPACING.s,
        width: '100%', padding: `${SPACING.s}px ${SPACING.m}px`,
        fontSize: FONT.md, color: S[800],
        background: 'transparent', border: 'none',
        cursor: 'pointer', textAlign: 'left',
        fontFamily: 'inherit',
      }}
      className="v4-overflow-item"
    >
      <Icon size={13} aria-hidden="true" style={{ color: S[500] }} />
      <span style={{ flex: 1 }}>{label}</span>
      {opts.shortcut && <kbd style={kbdMini}>{opts.shortcut}</kbd>}
    </button>
  );

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="More actions"
        aria-label="More actions"
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 28, height: 28, padding: 0,
          color: S[600], background: '#ffffff',
          border: `1px solid ${S[200]}`, borderRadius: RADIUS.md,
          cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        <MoreHorizontal size={13} aria-hidden="true" />
      </button>
      {open && (
        <div
          ref={popoverRef}
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            minWidth: 200,
            background: '#ffffff',
            border: `1px solid ${S[200]}`,
            borderRadius: RADIUS.lg,
            boxShadow: SHADOW.popover,
            zIndex: Z.popover,
            padding: SPACING.xs,
            display: 'flex', flexDirection: 'column', gap: 0,
          }}
        >
          {item('Re-layout canvas', RotateCcw, onRelayout)}
          {item('Save As… (clone)', FileJson, onSaveAs)}
          {item('New workflow…', FileJson, onNew)}
          <div style={{ height: 0.5, background: S[200], margin: `${SPACING.xs}px 0` }} />
          {item('Open in V3', ExternalLink, () => {
            if (typeof onOpenInV3 === 'function') onOpenInV3();
            else if (v3Href) window.location.href = v3Href;
          })}
        </div>
      )}
    </div>
  );
}

const kbdMini = {
  fontSize: FONT.xs, fontWeight: WEIGHT.semibold,
  color: S[500], background: S[100],
  padding: '1px 5px', borderRadius: RADIUS.sm,
  fontFamily: 'inherit',
};
